import isNil from 'lodash/isNil'
import _get from 'lodash/get'
import assign from 'lodash/assign'
import PropTypes from 'prop-types'
import { Map } from 'immutable'
import { useSelector, useDispatch } from 'react-redux'
import React, { useCallback, useEffect } from 'react'
import { pushHistory } from '../../../services/navigation/actions'
import { selectResolver } from '../../../services/resolver/selectors'
import { GIFT_READY_STATE } from '../../../services/gift/index'
import {
  clearRecipientEmailInfo,
  setGiftDetailsPersistent,
} from '../../../services/gift/actions'
import {
  GIFT_SUBROUTE_PAYMENT,
  useNextGiftRoute,
} from '../../../services/gift/utils/index'
import {
  selectGiftDetails,
  selectUserCanPurchaseGift,
  selectUserCanPurchaseGiftMultipleCurrency,
  selectGiftPlan,
  selectGiftSku,
} from '../../../services/gift/selectors'
import { selectAuthorized, selectAuth } from '../../../services/auth/selectors'
import { selectPrimaryLanguage } from '../../../services/user/selectors'
import { selectStaticTextByKey } from '../../../services/static-text/selectors'
import { GiftSelection } from '../GiftSelection/index'
import { GiftLayout } from '../GiftLayout/index'
import GiftForm from '../GiftForm/index'
import GiftFormV2 from '../GiftForm/GiftFormV2'
import GiftPlaceholder, { TYPES } from '../GiftPlaceholder/index'
import { get as getConfig } from '../../../config/index'
import { BILLING_PLAN_GRID_LOCATION_GIFT } from '../../../services/plans/utils'
import { useGetPlanGridData } from '../../../services/plans/hooks'
import { GIFT_CREATE_PAGE_STATIC_KEY as staticTextStoreKey } from './gift-create-page-static-key'

const config = getConfig()
const planGraphQLActive = !!_get(config, ['features', 'checkout', 'planGraphQL'])
const giftMultipleCurrenciesActive = !!_get(config, ['features', 'gift', 'multipleCurrencies'])

const GiftCreatePagePropTypes = {
  location: PropTypes.object,
  history: PropTypes.object,
}

/**
 * @type {React.FC<PropTypes.InferProps<GiftCreatePagePropTypes>>}
 * @returns {React.ReactElement}
 * */
export function GiftCreatePage () {
  const auth = useSelector(selectAuth)
  const authJwt = auth.get('jwt')
  const staticText = useSelector(selectStaticTextByKey(staticTextStoreKey))
  const canPurchaseGift = useSelector(
    giftMultipleCurrenciesActive
      ? selectUserCanPurchaseGiftMultipleCurrency
      : selectUserCanPurchaseGift,
  )
  const resolver = useSelector(selectResolver)
  const loggedIn = useSelector(selectAuthorized)
  const gift = useSelector(selectGiftDetails)
  const sku = useSelector(selectGiftSku)
  let selectedPlan = useSelector(selectGiftPlan)
  const getNextRoute = useNextGiftRoute()
  const dispatch = useDispatch()
  const userLanguage = useSelector(selectPrimaryLanguage)
  const formattedPlans = useGetPlanGridData({
    userLanguage,
    authJwt,
    page: BILLING_PLAN_GRID_LOCATION_GIFT,
  })

  if (planGraphQLActive) {
    selectedPlan = formattedPlans.find((gp) => gp.get('sku') === sku) || Map()
  }

  const sendByEmail = resolver.getIn([
    'query', 'deliveryType']) === 'email'

  const onToggleEmailFields = useCallback(() => {
    dispatch(pushHistory({
      scrollToTop: false,
      url: resolver.getIn(['data', 'path']),
      query: !sendByEmail
        ? { deliveryType: 'email' }
        : {},
    }))
  }, [resolver, sendByEmail])

  const onValidSubmit = useCallback(
    (giftData) => {
      const data = assign({}, gift.toJS(), giftData, { planCurrency: selectedPlan.get('currencyIso') })
      const nextRoute = getNextRoute({ subRoute: GIFT_SUBROUTE_PAYMENT })
      dispatch(setGiftDetailsPersistent(data))
      dispatch(pushHistory({
        scrollToTop: true,
        url: nextRoute,
      }))
    },
    [gift, dispatch, pushHistory, getNextRoute, selectedPlan],
  )

  useEffect(() => {
    // to reset recipient email if not used
    if (!sendByEmail) {
      dispatch(clearRecipientEmailInfo())
    }
  }, [dispatch, sendByEmail])

  const pageReady = !isNil(sku)
  const processing = loggedIn && (canPurchaseGift === GIFT_READY_STATE.NOT_READY)
  const restricted = giftMultipleCurrenciesActive
    ? canPurchaseGift !== GIFT_READY_STATE.CAN_PURCHASE
    : canPurchaseGift === GIFT_READY_STATE.NOT_READY
      || canPurchaseGift === GIFT_READY_STATE.CANNOT_PURCHASE

  if (planGraphQLActive && (!selectedPlan || (Map.isMap(selectedPlan) && !selectedPlan.size))) {
    return (
      <GiftLayout
        restricted={restricted}
        pageTitle={staticText.get('title')}
        processing={processing}
        pageReady={pageReady}
        main={
          <div style={{ width: '90%' }}>
            <GiftPlaceholder type={TYPES.SMALL} />
            <GiftPlaceholder type={TYPES.MEDIUM} />
            <GiftPlaceholder type={TYPES.LARGE} />
            <GiftPlaceholder type={TYPES.MEDIUM} />
            <GiftPlaceholder type={TYPES.SMALL} />
          </div>
        }
        rightSide={
          <div style={{ width: '80%', margin: '0px auto' }}>
            <GiftPlaceholder type={TYPES.SMALL} />
            <GiftPlaceholder type={TYPES.SMALL} />
            <GiftPlaceholder type={TYPES.LARGE} />
          </div>
        }
      />
    )
  }

  return (
    <GiftLayout
      restricted={restricted}
      pageTitle={staticText.get('title')}
      processing={processing}
      pageReady={pageReady}
      main={
        giftMultipleCurrenciesActive
          ? <GiftFormV2
            toggleEmailFields={onToggleEmailFields}
            canPurchaseGift={canPurchaseGift}
            sendByEmail={sendByEmail}
            submit={onValidSubmit}
            loggedIn={loggedIn}
            editing={true}
          />
          : <GiftForm
            toggleEmailFields={onToggleEmailFields}
            canPurchaseGift={canPurchaseGift}
            sendByEmail={sendByEmail}
            submit={onValidSubmit}
            loggedIn={loggedIn}
            editing={true}
          />
      }
      rightSide={
        <GiftSelection
          canPurchaseGift={canPurchaseGift}
          selectedPlan={selectedPlan}
          planData={formattedPlans}
          changeable
        />
      }
    />
  )
}

GiftCreatePage.propTypes = GiftCreatePagePropTypes

export default GiftCreatePage
